"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useJsonResource } from "@/hooks/useJsonResource";
import { readBusinessCookie, writeBusinessCookie } from "@/lib/business/businessCookie";
import { publishWorkerSnapshot } from "@/lib/clientWorkerSnapshot";
import type { SessionUser, SidebarBusiness, SidebarFolder, SidebarWorker } from "./types";
import { APP_HOME } from "@/lib/appHome";

/**
 * The sidebar's three polled lists, the active business they are scoped to,
 * and the refreshes that follow a route change or a mutation.
 */
export function useSidebarData(pathname: string) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Start empty on both server and client to avoid hydration mismatch; the
  // cookie is applied by adoptBusinesses once the business list has loaded.
  const [activeBusiness, setActiveBusiness] = useState("");

  // Reconcile the active business with a freshly loaded list: the cookie
  // wins when it names a real business, then the current pick, then the
  // first business. The cookie is written back whenever it was missing or
  // stale, and router.refresh() re-renders the server components against it
  // so the SSR HTML matches the client's selection.
  function adoptBusinesses(list: SidebarBusiness[]) {
    if (list.length === 0) return;
    const fromCookie = readBusinessCookie();
    const next =
      (list.some((b) => b.id === fromCookie) ? fromCookie : null) ??
      (list.some((b) => b.id === activeBusiness) ? activeBusiness : null) ??
      list[0].id;
    if (next !== activeBusiness) {
      setActiveBusiness(next);
      // A valid cookie already scoped the server render. Only refresh when
      // correcting it, not just when initializing local sidebar state.
      if (next !== fromCookie) {
        writeBusinessCookie(next);
        window.dispatchEvent(new Event("caw:activity-refresh"));
        router.refresh();
      }
    } else if (!fromCookie) {
      writeBusinessCookie(next);
      window.dispatchEvent(new Event("caw:activity-refresh"));
    }
  }
  const { data: workerData, refresh: refreshWorkers } = useJsonResource<{
    workers: SidebarWorker[];
  }>("/api/workers", {
    interval: 15000,
    onData: (data) => publishWorkerSnapshot(data.workers ?? []),
  });
  const { data: folderData, refresh: refreshFolders } = useJsonResource<{
    folders: SidebarFolder[];
  }>("/api/folders", { interval: 15000 });
  const { data: businessData, refresh: refreshBusinesses } = useJsonResource<{
    businesses: SidebarBusiness[];
    user?: SessionUser;
  }>("/api/businesses", {
    interval: 15000,
    onData: (data) => adoptBusinesses(data.businesses ?? []),
  });
  const businesses = businessData?.businesses ?? [];
  const sessionInfo = businessData?.user ?? null;

  // Route changes refresh the counts next to each worker.
  useEffect(() => {
    const onRefresh = () => {
      void refreshWorkers();
      void refreshFolders();
      void refreshBusinesses();
    };
    onRefresh();
    window.addEventListener("caw:activity-refresh", onRefresh);
    window.addEventListener("caw:messages-refresh", onRefresh);
    return () => {
      window.removeEventListener("caw:activity-refresh", onRefresh);
      window.removeEventListener("caw:messages-refresh", onRefresh);
    };
  }, [pathname, refreshWorkers, refreshFolders, refreshBusinesses]);

  const scopedFolders = useMemo(
    () => (folderData?.folders ?? []).filter((f) => f.businessId === activeBusiness),
    [folderData, activeBusiness]
  );
  const scopedWorkers = useMemo(
    () => (workerData?.workers ?? []).filter((w) => w.businessId === activeBusiness),
    [workerData, activeBusiness]
  );

  async function refreshAll() {
    await Promise.all([refreshWorkers(), refreshFolders(), refreshBusinesses()]);
    router.refresh();
  }

  function switchBusiness(id: string) {
    setActiveBusiness(id);
    writeBusinessCookie(id);
    // A worker page belongs to one business; staying on it after switching
    // showed the old business's worker, status feed and all, under the new
    // business's sidebar. Leave it for the new business's home. Refresh in
    // every case: a navigation keeps the root layout, and Smithers' thread
    // is keyed on the business the layout read — so it stayed on the old
    // business until the next full reload.
    if (pathname.startsWith("/workers/")) router.push(APP_HOME);
    else if (pathname.startsWith("/monitoring") && searchParams.has("dept")) {
      const next = new URLSearchParams(searchParams.toString());
      next.delete("dept");
      router.push(`${pathname}${next.size ? `?${next}` : ""}`);
    }
    router.refresh();
    // Smithers' badge and thread poll every few seconds; do not make the
    // person watch the old business's numbers until the next tick.
    window.dispatchEvent(new Event("caw:activity-refresh"));
  }

  return {
    activeBusiness,
    businesses,
    sessionInfo,
    scopedFolders,
    scopedWorkers,
    refreshAll,
    refreshBusinesses,
    switchBusiness,
  };
}
